import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';

const quickLinks = [
  { label: '康永について', path: '/ja/about/core-advantages' },
  { label: 'ODM / OEM提携', path: '/ja/partnership' },
  { label: '提携フロー', path: '/ja/cooperation/process' },
  { label: 'サービス項目', path: '/ja/services/hot-formulas' },
  { label: '連絡先情報', path: '/ja/contact/info' },
];

const serviceItems = [
  { label: '人気処方', path: '/ja/services/hot-formulas' },
  { label: '天然原料', path: '/ja/services/natural-ingredients' },
  { label: '剤型', path: '/ja/services/dosage-forms' },
  { label: 'パッケージデザイン', path: '/ja/services/packaging' },
];

const contactInfo = [
  {
    icon: 'ri-map-pin-line',
    label: '住所',
    value: '台湾 台中市北区中清路一段79号6階',
    href: null,
  },
  {
    icon: 'ri-phone-line',
    label: '電話',
    value: '+886-4-2202-9858',
    href: 'tel:+886422029858',
  },
  {
    icon: 'ri-mail-line',
    label: 'メール',
    value: 'natural1070606@gmail.com',
    href: 'mailto:natural1070606@gmail.com',
  },
];

const certifications = [
  { label: 'GMP' },
  { label: 'HACCP' },
  { label: 'ISO 22000' },
  { label: 'SGS' },
];

interface JaLayoutProps {
  children: ReactNode;
}

export default function JaLayout({ children }: JaLayoutProps) {
  const currentYear = new Date().getFullYear();

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">{children}</main>

      {/* Japanese Footer */}
      <footer className="bg-[#1a2e1a] text-white">
        <div className="max-w-[1280px] mx-auto px-6 xl:px-10 py-16">
          <div className="footer-grid">
            {/* Column 1 - Brand */}
            <div>
              <div className="flex items-center gap-3 mb-5">
                <img
                  src="https://static.readdy.ai/image/c58d2dc5fdc4f4caa90264537c733184/e260b0535e420cf4debd81c8e514ae3f.png"
                  alt="Kang Yong Biotech ロゴ"
                  className="h-10 w-auto object-contain brightness-0 invert"
                />
              </div>
              <h2 className="text-xl font-bold text-white mb-3">康永生技（Kang Yong Biotech）</h2>
              <p className="text-gray-300 text-[0.9375rem] leading-relaxed">
                健康食品・サプリメント<br />
                OEM/ODM 専門メーカー
              </p>
              <p className="text-gray-400 text-[0.875rem] leading-relaxed mt-4">
                処方開発から完成品納品まで、<br />
                一貫した製造サービスを提供します。
              </p>
            </div>

            {/* Column 2 - Quick Links */}
            <div>
              <h3 className="text-[1.125rem] font-semibold text-white mb-5">クイックリンク</h3>
              <ul className="space-y-3">
                {quickLinks.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="text-gray-300 text-[0.9375rem] hover:text-white transition-colors duration-200 flex items-center gap-2 group cursor-pointer"
                    >
                      <span className="w-0 h-0.5 bg-white transition-all duration-200 group-hover:w-3 flex-shrink-0" />
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 3 - Services */}
            <div>
              <h3 className="text-[1.125rem] font-semibold text-white mb-5">サービス内容</h3>
              <ul className="space-y-3">
                {serviceItems.map((item) => (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className="text-gray-300 text-[0.9375rem] hover:text-white transition-colors duration-200 flex items-center gap-2 group cursor-pointer"
                    >
                      <span className="w-0 h-0.5 bg-white transition-all duration-200 group-hover:w-3 flex-shrink-0" />
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 4 - Contact */}
            <div>
              <h3 className="text-[1.125rem] font-semibold text-white mb-5">連絡先</h3>
              <ul className="space-y-4">
                {contactInfo.map((item) => (
                  <li key={item.label} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className={`${item.icon} text-[1.125rem] text-gray-300`} />
                    </div>
                    <div>
                      <span className="text-gray-400 text-[0.8125rem] block">{item.label}</span>
                      {item.href ? (
                        <a
                          href={item.href}
                          rel="nofollow"
                          className="text-gray-300 text-[0.875rem] hover:text-white transition-colors duration-200 cursor-pointer"
                        >
                          {item.value}
                        </a>
                      ) : (
                        <span className="text-gray-300 text-[0.875rem]">{item.value}</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 5 - LINE */}
            <div className="min-w-0">
              <h3 className="text-[1.125rem] font-semibold text-white mb-5">LINE 公式アカウント</h3>
              <div className="flex flex-col items-start gap-3">
                <a
                  href="https://lin.ee/X7Hcpf4"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="flex-shrink-0 cursor-pointer"
                  aria-label="LINEに追加"
                >
                  <img
                    src="https://storage.readdy-site.link/project_files/4ba0dab9-dd17-40bb-9c01-736e9bd30d9b/caddd531-3b1d-4c43-9423-d53ab0804cd5_L_gainfriends_2dbarcodes_GW.png?v=7268c853782cd8d95252096de5ccf381"
                    alt="康永生技 LINE QRコード"
                    className="w-[100px] h-[100px] object-contain rounded-lg bg-white p-1"
                  />
                </a>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-chat-smile-2-line text-[1rem] text-[#4ade80]" />
                    </div>
                    <span className="text-gray-300 text-[0.9375rem] font-medium">@126uwznp</span>
                  </div>
                  <p className="text-gray-400 text-[0.75rem] leading-relaxed mb-3">
                    QRコードをスキャンしてLINEに追加、<br className="hidden xl:block" />
                    OEM/ODMのお問い合わせはこちらから
                  </p>
                  <a
                    href="https://lin.ee/X7Hcpf4"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="inline-flex items-center gap-2 bg-[#4ade80]/15 hover:bg-[#4ade80]/25 border border-[#4ade80]/40 text-[#4ade80] text-[0.8125rem] font-medium px-3 py-1.5 rounded-lg transition-colors duration-200 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-chat-smile-2-line text-[0.9375rem]" />
                    LINEに追加
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-white/10">
          <div className="max-w-[1280px] mx-auto px-6 xl:px-10 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
              <p className="text-gray-400 text-[0.8125rem] text-center sm:text-left">
                &copy; {currentYear} Kang Yong Biotech Ltd.（康永生技有限公司）
              </p>
              <span className="hidden sm:inline text-gray-600 text-[0.75rem]">|</span>
              <Link
                to="/ja/privacy-policy"
                className="text-gray-400 text-[0.8125rem] hover:text-gray-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                プライバシーポリシー
              </Link>
              <span className="hidden sm:inline text-gray-600 text-[0.75rem]">|</span>
              <Link
                to="/ja/terms-of-use"
                className="text-gray-400 text-[0.8125rem] hover:text-gray-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                利用規約
              </Link>
            </div>
            <div className="flex items-center gap-4 flex-wrap justify-center">
              {certifications.map((c) => (
                <span key={c.label} className="text-gray-400 text-[0.75rem] border border-white/20 px-2.5 py-0.5 rounded-full">{c.label}</span>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
